/**
 * In-store vendors on the client (B17, ADR 0006). Pure helpers shared by the
 * admin Vendors pages and the vendor portal. The server (lib/marketplace/
 * vendorStates.js) decides every transition again; this only shapes the UI.
 */

/** Vendor review actions: which statuses offer them, and whether a reason is required. */
export const VENDOR_ACTIONS = {
  approve: { label: 'Approve', from: ['pending', 'rejected'], tone: 'primary', done: 'Vendor approved' },
  reject: { label: 'Reject', from: ['pending'], tone: 'danger', note: true, done: 'Application rejected' },
  suspend: { label: 'Suspend', from: ['approved'], tone: 'danger', note: true, done: 'Vendor suspended' },
  reinstate: { label: 'Reinstate', from: ['suspended'], tone: 'primary', done: 'Vendor reinstated' },
};

/** The review actions open to a vendor in `status`, in display order. */
export const actionsFor = (status) => Object.entries(VENDOR_ACTIONS).filter(([, a]) => a.from.includes(status)).map(([key, a]) => ({ key, ...a }));

export const VENDOR_TABS = [
  { key: 'pending', label: 'Requests' },
  { key: 'approved', label: 'Approved' },
  { key: 'suspended', label: 'Suspended' },
  { key: 'rejected', label: 'Rejected' },
  { key: '', label: 'All' },
];

export const PRODUCT_TABS = [
  { key: 'pending', label: 'Awaiting review' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: '', label: 'All' },
];

/** "12.5%" | "Store default (10%)" | "Not set". */
export function pctLabel(pct, fallback) {
  if (pct != null && pct !== '') return `${Number(pct)}%`;
  if (fallback != null && fallback !== '') return `Default (${Number(fallback)}%)`;
  return 'Not set';
}

/** A commission field → { value } (number or null for "use the default") or { error }. */
export function parsePct(input) {
  const s = String(input ?? '').trim();
  if (s === '') return { value: null };
  const n = Number(s);
  if (!Number.isFinite(n) || n < 0 || n > 100) return { error: 'Enter a percentage from 0 to 100, or leave it empty.' };
  return { value: Math.round(n * 100) / 100 };
}

/** The rate a vendor product earns the store: product → vendor → store default (D75). */
export const effectivePct = (productPct, vendorPct, storePct) => [productPct, vendorPct, storePct].find((p) => p != null && p !== '') ?? null;

/** Rupees string → paise (null when blank or invalid). */
export function rupeesToPaise(v) {
  const s = String(v ?? '').replace(/[,\s₹]/g, '');
  if (s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : null;
}

export const paiseToRupees = (p) => (p == null ? '' : String(Number(p) / 100));

/** Group products by "Category › Subcategory" for the admin's vendor page, in name order. */
export function groupByCategory(products = []) {
  const groups = new Map();
  for (const p of products) {
    const name = [p.category && p.category.name, p.subcategory && p.subcategory.name].filter(Boolean).join(' › ') || 'Uncategorised';
    if (!groups.has(name)) groups.set(name, []);
    groups.get(name).push(p);
  }
  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([name, items]) => ({ name, items }));
}
